import React, { useEffect, useState } from 'react';
import { Alert, Typography } from '@mui/material';
import proveedorService from '../services/proveedorService';
import { normalizarNotasProveedor } from '../utils/proveedorNotas';

interface Props {
  /** Si hay proveedor seleccionado, se muestra su Nota 1 cuando exista. */
  nroProveedor?: number | null;
  /** Si ya se conoce el valor (p. ej. del GET), evita un fetch extra. */
  notas?: string | null;
}

/**
 * Aviso de Nota 1 (uso interno) del proveedor en pantallas de acción
 * (órdenes de pago, facturas de compra, etc.).
 */
const ProveedorNotasAvisos: React.FC<Props> = ({ nroProveedor, notas }) => {
  const [notasCargadas, setNotasCargadas] = useState(() => normalizarNotasProveedor(notas));

  useEffect(() => {
    const conocidas = normalizarNotasProveedor(notas);
    if (conocidas) {
      setNotasCargadas(conocidas);
      return;
    }

    const nro = Number(nroProveedor);
    if (!Number.isFinite(nro) || nro <= 0) {
      setNotasCargadas('');
      return;
    }

    // Caller pasó el resultado del GET (vacío): no hay Nota 1.
    if (notas != null) {
      setNotasCargadas('');
      return;
    }

    setNotasCargadas('');
    let cancelled = false;
    (async () => {
      try {
        const proveedor = await proveedorService.getProveedor(nro);
        if (!cancelled) {
          setNotasCargadas(normalizarNotasProveedor(proveedor.notas));
        }
      } catch {
        if (!cancelled) setNotasCargadas('');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [nroProveedor, notas]);

  if (!notasCargadas) return null;

  return (
    <Alert severity="info" variant="outlined" sx={{ py: 0.5, alignItems: 'flex-start', height: '100%' }}>
      <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 0.25 }}>
        Nota 1 (uso interno)
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {notasCargadas}
      </Typography>
    </Alert>
  );
};

export default ProveedorNotasAvisos;
